import { Injectable, effect, inject } from '@angular/core';
import { CaseContextService } from './case-context.service';
import { CaseApiService } from './case-api.service';
import { RateApiService } from './rate-api.service';
import { Case } from './case.model';

/** Computes the Tier 1 fit-set across FA+IA+RILA whenever a case becomes
 *  active, calls the touch endpoint with those IDs, and surfaces the
 *  returned diff to CaseContextService. Idempotent per activation —
 *  the activation guard ensures we only compute diff once per setActive. */
@Injectable({ providedIn: 'root' })
export class CaseDiffService {
  private caseCtx = inject(CaseContextService);
  private caseApi = inject(CaseApiService);
  private rateApi = inject(RateApiService);

  private lastProcessedKey: string | null = null;

  constructor() {
    effect(() => {
      const c = this.caseCtx.activeCase();
      if (!c) {
        this.lastProcessedKey = null;
        return;
      }
      // Key includes lastModifiedAt so edits trigger a re-diff even with the same case ID
      const key = `${c.id}|${c.lastModifiedAt}`;
      if (this.lastProcessedKey === key) return;
      this.lastProcessedKey = key;
      this.computeAndTouch(c);
    });
  }

  private async computeAndTouch(c: Case): Promise<void> {
    try {
      const [fa, ia, rila] = await Promise.all([
        this.rateApi.getFixedAnnuityRates(),
        this.rateApi.getIndexedAnnuityRates(),
        this.rateApi.getIndexedVariableRates(),
      ]);

      const fits: number[] = [];
      for (const r of fa) if (this.passesHard(r, c) && r.benefitApiProductId != null) fits.push(r.benefitApiProductId);
      for (const r of ia) if (this.passesHard(r, c) && r.product_id != null) fits.push(r.product_id);
      for (const r of rila) if (this.passesHard(r, c) && r.product_id != null) fits.push(r.product_id);

      const diff = await this.caseApi.touch(c.id, fits).toPromise();
      if (diff) this.caseCtx.setDiff(diff);
    } catch (err) {
      console.warn('[case-diff] failed to compute or send diff', err);
    }
  }

  private passesHard(r: any, c: Case): boolean {
    if (c.state) {
      const states: string[] = r.states ?? [];
      if (states.length > 0 && !states.includes(c.state)) return false;
    }
    if (c.premium != null) {
      if (Array.isArray(r.bands) && r.bands.length > 0) {
        const lowestMin = Math.min(...r.bands.map((b: any) => b.min));
        if (lowestMin > c.premium) return false;
      } else if (r.minContribution != null && r.minContribution > c.premium) {
        return false;
      }
    }
    if (c.age != null && r.maxIssueAge != null && r.maxIssueAge < c.age) return false;
    return true;
  }
}
